import { Offcanvas, Row } from 'react-bootstrap';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import FormTextField from './formFields/FormTextField';
import FormSelectField from './formFields/FormSelectField';
import FormCheckboxField from './formFields/FormCheckboxField';
import FormDateTimeField from './formFields/FormDateTimeField';
import RelationshipPickerField from './formFields/RelationshipPickerField';
import { ACTION_STATUS_OPTIONS, IapScope } from './ICSConstants';
import ICSChecklist from './ICSChecklist';
import DeleteModal from '../DesignSystems/DeleteModal';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import { ActionBase } from '../../types/iapBaseTypes';
import {
  ActionEntity,
  ActionFormValues,
  useScopedActionForm,
} from './hooks/useScopedActionForm';

type FormFields = ActionBase & {
  poc: string;
  briefed_poc: boolean;
  start_date: Date;
  status: string;
  target_date: Date;
  actual_date: Date;
  resources: string[];
};

type ActionDrawerProps = {
  scope: IapScope;
  selectedEntity?: ActionEntity;
  toggle: () => void;
};

const ActionDrawer: React.FC<ActionDrawerProps> = ({
  scope,
  selectedEntity,
  toggle,
}) => {
  const isEdit = !!selectedEntity;
  const adapter = useScopedActionForm(scope);

  const form = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: isEdit
      ? {
          ...(selectedEntity as any),
          resources: (selectedEntity as any).resources || [],
        }
      : { status: 'Planned', resources: [] },
  });
  const { handleSubmit, watch } = form;
  const watchedName = watch('name');

  const [checklist, setChecklist] = useState<
    { label: string; checked: boolean }[]
  >(
    (
      selectedEntity?.checklist as {
        label: string;
        checked?: boolean;
        status?: string;
      }[]
    )?.map((item) => ({
      label: item.label,
      checked:
        item.checked ??
        (item.status === 'Complete' || item.status === 'Closed'),
    })) ?? []
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const onSubmit = async (formData: FormFields) => {
    const values: ActionFormValues = { ...(formData as any), checklist };
    if (isEdit) {
      await adapter.update(selectedEntity as ActionEntity, values);
    } else {
      await adapter.create(values);
    }
    toggle();
  };

  const handleDeactivateClick = () => {
    if (!selectedEntity?.archived) {
      setShowConfirmModal(true);
    } else {
      void toggleActive();
    }
  };

  const toggleActive = async () => {
    if (selectedEntity) {
      await adapter.archive(selectedEntity, !selectedEntity.archived);
    }
    setShowConfirmModal(false);
    toggle();
  };

  return (
    <DrawerWrapper
      toggle={toggle}
      title={isEdit ? 'Edit Action' : 'Add Action'}
    >
      <FormProvider {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 250,
                    message: 'This maximum length is 250 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Point of Contact"
                fieldName="poc"
                options={adapter.pocOptions}
                variant="half"
                isClearable
              />
              <FormCheckboxField
                title="Briefed POC"
                fieldName="briefed_poc"
                variant="half"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Status"
                fieldName="status"
                options={ACTION_STATUS_OPTIONS}
                variant="full"
                rules={{
                  required: { value: true, message: 'Status is required' },
                }}
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Start Date & Time"
                fieldName="start_date"
                variant="full"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Target Date & Time"
                fieldName="target_date"
                variant="full"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Actual Date & Time"
                fieldName="actual_date"
                variant="full"
              />
            </Row>
            {adapter.showResources && (
              <RelationshipPickerField
                label="Resources"
                fieldName="resources"
                defaultValue={(selectedEntity as any)?.resources || []}
                options={adapter.resourceOptions}
                identity={(resource: any) => resource.id}
                title={(resource: any) =>
                  `${resource.name} (${resource.quantity} ${resource.kind})`
                }
                cardType="resource-set"
              />
            )}
            <div className="mt-2">
              <label className="form-label d-flex align-items-center">
                <span className="me-2">Checklist</span>
                <SharedIcon iconName="info" size="sm" />
                <span className="color-muted" style={{ fontSize: '0.675rem' }}>
                  Checklists are not editable after creation.
                </span>
              </label>
              <ICSChecklist
                checklist={checklist}
                setChecklist={setChecklist}
                editable={!isEdit}
                existingData={isEdit}
              />
            </div>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              {isEdit && (
                <StylishNewButton
                  className="button--tertiary button--reverse"
                  onClick={handleDeactivateClick}
                >
                  {(!selectedEntity?.archived && 'Deactivate') || 'Activate'}
                </StylishNewButton>
              )}
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={(!isEdit && !watchedName) || adapter.isSaving}
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>

      {showConfirmModal && (
        <DeleteModal
          title="Confirm Deactivation"
          onClose={() => setShowConfirmModal(false)}
          onConfirmDelete={toggleActive}
        >
          <div className="mb-3">
            Are you sure you want to deactivate this action?
          </div>
          <div className="mb-3">
            Deactivated actions will be hidden from the active actions tracker
            but preserved in the system for record-keeping.
          </div>
        </DeleteModal>
      )}
    </DrawerWrapper>
  );
};

export default ActionDrawer;
